// Compliance > Federal Exclusion filters (PD-13954).
//
// The Federal Exclusion list is built by the crm-api FederalExclusionService
// from a cross-database read (the tenant's client_exclusion_match joined to
// the OIG list in mission control), so these filters are read by key off the
// request's `filters` payload — not through applyFiltersToQueryBuilder.
// `objectTable: 'lm_exclusion_match'` is the list's stable scope key, kept from
// the first build; it names no table. Option ids are the server's codes.

import { createKeywordFilter } from './factories';
import type { FilterConfig, FilterRegistry } from './types';

export const FEDERAL_EXCLUSION_FILTER_LIST_KEY = 'license_exclusion';
const LIST_KEYS: ReadonlyArray<string> = [FEDERAL_EXCLUSION_FILTER_LIST_KEY];

const custom = {
	objectTable: 'lm_exclusion_match',
	joinType: 'custom' as const,
};

const keywordFilter: FilterConfig = {
	...createKeywordFilter({
		label: 'Search',
		placeholder: 'Account, person, OIG name or NPI...',
		objectTable: 'lm_exclusion_match',
		listKeys: [...LIST_KEYS],
	}),
	queryParamName: 'exclusionKeywords',
	queryMapping: custom,
};

export const licenseExclusionFilters: FilterRegistry = {
	exclusionKeywords: keywordFilter,

	// Which tab: each tab locks (and hides) it, so the tab is the view.
	exclusionView: {
		type: 'radio',
		label: 'Matches',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'exclusionView',
		multiple: false,
		options: [
			{ id: 'strong', label: 'Same NPI, or same name at the same address' },
			{ id: 'name_only', label: 'Same name in the same state' },
			{ id: 'reviewed', label: 'Reviewed' },
		],
		queryMapping: custom,
		format: 'single',
		valueExtractor: { property: 'id' },
		listKeys: [...LIST_KEYS],
	},

	exclusionMatchedOn: {
		type: 'multi-select',
		label: 'Matched on',
		placeholder: 'Select how it matched...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: [
			{ id: 'NPI', label: 'Same NPI' },
			{ id: 'NAME_ADDRESS', label: 'Same name at the same address' },
			{ id: 'NAME_STATE', label: 'Same name, same state' },
		],
		queryParamName: 'exclusionMatchedOn',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: custom,
		format: 'array',
		listKeys: [...LIST_KEYS],
	},

	exclusionSubject: {
		type: 'multi-select',
		label: 'Who matched',
		placeholder: 'Account or linked person...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: [
			{ id: 'account', label: 'The account' },
			{ id: 'person', label: 'A linked person (pharmacist in charge, owner)' },
		],
		queryParamName: 'exclusionSubject',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: custom,
		format: 'array',
		listKeys: [...LIST_KEYS],
	},
};
